'use client';

import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const installerTypes = [
{
  title: 'Site Installers',
  img: 'https://www.nexsys.group/assets/modular-crane-construction-DzFZmVYi.png',
  intro: 'Site installers are trained partners responsible for erecting, assembling, and installing NeXSYS components or modules on site in accordance with system guidelines and quality standards.',
  benefits: [
  'Training and certification in safe, efficient system installation.',
  'Reduced installation errors and rework.',
  'Faster project completion through standardized procedures.',
  'Assurance of compliance with system quality standards.',
  'Support from NeXSYS technical team for complex projects.']

},
{
  title: 'Site Finishers',
  img: 'https://www.nexsys.group/assets/benefits-hero-DMTBAPLn.jpg',
  intro: 'Site finishers complete final works after installation, including internal finishes, interfaces, and detailing, ensuring the completed building meets performance, aesthetic, and client requirements.',
  benefits: [
  'Training in modular-specific finishing techniques.',
  'High-quality finishes aligned with system standards.',
  'Reduced on-site delays and defects.',
  'Improved client satisfaction through consistent quality.',
  'Access to NeXSYS technical guidance for finishing integration.']

}];


const supportItems = [
'Certified installation training',
'Quality assurance standards',
'Technical support access',
'Safety compliance protocols',
'Project coordination support',
'Ongoing skills development'];


export default function InstallersSettersPage() {
  const [formData, setFormData] = useState({ name: '', company: '', email: '', type: 'Select Installer Type' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async () => {
    if (!formData?.name || !formData?.company || !formData?.email || formData?.type === 'Select Installer Type') {
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData?.name,
          company: formData?.company,
          email: formData?.email,
          projectType: formData?.type,
          message: `Partner enquiry from Installers & Setters page. Installer Type: ${formData?.type}`,
        }),
      });
      if (res?.ok) {
        setStatus('success');
        setFormData({ name: '', company: '', email: '', type: 'Select Installer Type' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="pt-24 pb-12 bg-gradient-dark">
          <div className="container mx-auto container-padding">
            <div className="max-w-3xl">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">Installers &amp; Setters</span>
              <h1 className="text-hero lg:text-hero-lg font-heading font-bold text-white mt-2 mb-6">
                Partner with <span className="font-bold text-white">NeXSYS</span>
              </h1>
              <p className="text-xl text-white/80 leading-relaxed">
                Join our network of certified installers and finishers delivering quality construction across global markets.
              </p>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="section-padding bg-card">
          <div className="container mx-auto container-padding max-w-4xl">
            <h2 className="text-section lg:text-section-lg font-heading font-bold text-foreground mb-6">
              Expert Installation, Exceptional Results
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The <span className="font-bold text-nexsys-brand">NeXSYS</span> system is designed for efficient on-site assembly and finishing. Our certified installers and setters ensure every project meets the highest standards of quality, safety, and performance. Whether you specialise in structural installation or interior finishing, there is a clear pathway to becoming a trusted <span className="font-bold text-nexsys-brand">NeXSYS</span> partner.
            </p>
          </div>
        </section>

        {/* Partnership Opportunities */}
        <section className="section-padding">
          <div className="container mx-auto container-padding">
            <div className="mb-12 lg:mb-16 text-center">
              <h2 className="text-section lg:text-section-lg font-heading font-bold text-foreground mb-4">
                Partnership Opportunities
              </h2>
              <p className="text-lg text-muted-foreground">
                Join the <span className="font-bold text-nexsys-brand">NeXSYS</span> installation network.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-8">
              {installerTypes?.map((type) =>
              <div key={type?.title} className="bg-card rounded-xl overflow-hidden shadow-card border border-border">
                  <div className="p-6">
                    <h3 className="font-heading font-bold text-xl text-foreground mb-3">{type?.title}</h3>
                    <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                      {type?.intro?.split('NeXSYS')?.map((part, i, arr) =>
                    <React.Fragment key={i}>
                          {part}
                          {i < arr?.length - 1 && <span className="font-bold text-nexsys-brand">NeXSYS</span>}
                        </React.Fragment>
                    )}
                    </p>
                    <p className="font-semibold text-foreground text-sm mb-2">Benefits:</p>
                    <ul className="space-y-2">
                      {type?.benefits?.map((b) =>
                    <li key={b} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary flex-shrink-0 mt-0.5">
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                          {b?.split('NeXSYS')?.map((part, i, arr) =>
                      <React.Fragment key={i}>
                              {part}
                              {i < arr?.length - 1 && <span className="font-bold text-nexsys-brand">NeXSYS</span>}
                            </React.Fragment>
                      )}
                        </li>
                    )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Support Section */}
        <section className="section-padding bg-nexsys-surface">
          <div className="container mx-auto container-padding">
            <div className="mb-12 text-center">
              <h2 className="text-section lg:text-section-lg font-heading font-bold text-foreground mb-4">
                Installer Partner Support
              </h2>
              <p className="text-lg text-muted-foreground">We invest in our partners&apos; success.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {supportItems?.map((item) =>
              <div key={item} className="bg-card rounded-xl p-4 shadow-card border border-border flex items-center gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary flex-shrink-0">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  <span className="text-sm font-medium text-foreground">{item}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="section-padding bg-gradient-dark">
          <div className="container mx-auto container-padding">
            <div className="max-w-xl mx-auto">
              <h3 className="text-2xl font-heading font-bold text-white mb-8 text-center">
                Become an Installer Partner
              </h3>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                {status === 'success' ? (
                  <div className="text-center py-6">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary mx-auto mb-4">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                    <p className="text-white text-lg font-semibold">Thank you! We&apos;ll be in touch soon.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Name"
                      value={formData?.name}
                      onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-primary" />
                    <input
                      type="text"
                      placeholder="Company"
                      value={formData?.company}
                      onChange={(e) => setFormData({ ...formData, company: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-primary" />
                    <input
                      type="email"
                      placeholder="Email"
                      value={formData?.email}
                      onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-primary" />
                    <select
                      value={formData?.type}
                      onChange={(e) => setFormData({ ...formData, type: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-primary">
                      <option value="Select Installer Type">Select Installer Type</option>
                      <option value="Site Installers">Site Installers</option>
                      <option value="Site Finishers">Site Finishers</option>
                    </select>
                    {status === 'error' && (
                      <p className="text-red-400 text-sm">Please fill in all fields and select an installer type.</p>
                    )}
                    <button
                      onClick={handleSubmit}
                      disabled={status === 'loading'}
                      className="w-full bg-banana text-banana-foreground font-semibold py-3 rounded-lg hover:bg-banana/90 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60">
                      {status === 'loading' ? 'Sending...' : 'Send'}
                      {status !== 'loading' && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m22 2-7 20-4-9-9-4Z" />
                          <path d="M22 2 11 13" />
                        </svg>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );

}